import React, { useState, useEffect, useCallback } from 'react';
import { Route, useHistory, useLocation, Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import decode from 'jwt-decode';
import { logOut } from '../actions/auth';

export const ProtectedRoutes = ({ component: Component, path, ...rest }) => {
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('profile'));
        } catch {
            return null;
        }
    });
    const [isTokenValid, setIsTokenValid] = useState(true);
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    // Memoized logout function to prevent unnecessary re-renders
    const logout = useCallback(() => {
        const currentUser = JSON.parse(localStorage.getItem('profile') || 'null');
        if (currentUser?.username) {
            dispatch(logOut(currentUser.username, history));
        }
        setUser(null);
        setIsTokenValid(false);
        localStorage.removeItem('profile');
    }, [dispatch, history]);

    // Validate token function
    const validateToken = useCallback((token) => {
        if (!token) return false;
        
        try {
            const decodedToken = decode(token);
            const currentTime = Date.now() / 1000; // Convert to seconds
            
            // Add 5 minute buffer before expiration
            return decodedToken.exp > (currentTime + 300);
        } catch (error) {
            console.warn('Invalid token format:', error);
            return false;
        }
    }, []);

    // Check authentication status
    useEffect(() => {
        const checkAuth = () => {
            try {
                const storedProfile = localStorage.getItem('profile');
                if (!storedProfile) {
                    setUser(null);
                    setIsTokenValid(false);
                    return;
                }

                const profile = JSON.parse(storedProfile);
                const token = profile?.accessToken;

                if (!token || !validateToken(token)) {
                    // Token is invalid or expired
                    logout();
                    return;
                }

                // Update user state only if it's different
                if (!user || user.accessToken !== token) {
                    setUser(profile);
                    setIsTokenValid(true);
                }
            } catch (error) {
                console.error('Error checking authentication:', error);
                logout();
            }
        };

        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, validateToken, logout]);

    // Handle storage events (for multi-tab logout)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'profile' && !e.newValue) {
                // Profile was removed from localStorage
                setUser(null);
                setIsTokenValid(false);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    return (
        <Route
            {...rest}
            render={(props) => {
                // Show loading state briefly to prevent flash
                if (user === undefined) {
                    return <div>Loading...</div>;
                }

                return (
                    user && isTokenValid 
                        ? <Component /> 
                        : <Redirect to={{ 
                            pathname: "/login", 
                            state: { from: props.location }
                        }} />
                );
            }}
        />
    );
};
